import React, { useCallback, useState } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  Skeleton,
  Alert,
  Stack,
  IconButton,
} from '@mui/material';
import { School, ChevronLeft, ChevronRight } from '@mui/icons-material';
import { useListaPermitida } from '../../../authorization/useListaPermitida';
import { trainingService } from '../../../services/trainingService';
import type { Training } from '../../../types/training';
import {
  TrainingValidityStatus,
  TrainingValidityStatusLabels,
  TrainingValidityStatusColors,
  TrainingNRTypeLabels,
} from '../../../types/training';

interface EmployeeCardProps {
  employeeId: number;
  companyId: number;
}

const ACCENT = '#2E7D32';
const PAGE_SIZE = 5;

export const EmployeeTrainingsCard: React.FC<EmployeeCardProps> = ({ employeeId }) => {
  const [page, setPage] = useState(0);

  // Treinamento tem permissão própria: o cartão mostrava alerta vermelho de erro para quem
  // simplesmente não lê treinamento.
  const carregar = useCallback(
    () => trainingService.getTrainingsByEmployee(employeeId).then((res) => (Array.isArray(res) ? res : [])),
    [employeeId]
  );
  const lista = useListaPermitida<Training>('training.read', 'Treinamentos', carregar);
  const trainings = lista.itens;
  const loading = lista.carregando;

  const countByValidity = trainings.reduce<Record<string, number>>((acc, t) => {
    acc[t.validityStatus] = (acc[t.validityStatus] || 0) + 1;
    return acc;
  }, {});

  const totalPages = Math.ceil(trainings.length / PAGE_SIZE);
  const paged = trainings.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <Card variant="outlined" sx={{ height: '100%', borderTop: `3px solid ${ACCENT}` }}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header */}
        <Box display="flex" alignItems="center" gap={1} mb={2}>
          <Box sx={{ color: ACCENT, display: 'flex' }}><School fontSize="small" /></Box>
          <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
            Treinamentos
          </Typography>
          {!loading && !lista.erro && (
            <Chip label={trainings.length} size="small" sx={{ bgcolor: ACCENT, color: '#fff', fontWeight: 700 }} />
          )}
        </Box>

        {/* Body */}
        {loading && (
          <Stack spacing={1}>
            {[0, 1, 2].map((i) => <Skeleton key={i} variant="rounded" height={32} />)}
          </Stack>
        )}

        {lista.erro && (
          <Alert severity="error" variant="outlined" sx={{ py: 0.5 }}>{lista.erro}</Alert>
        )}

        {!loading && !lista.erro && trainings.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            {lista.bloqueio ?? 'Nenhum registro encontrado'}
          </Typography>
        )}

        {!loading && !lista.erro && trainings.length > 0 && (
          <>
            {/* Summary chips */}
            <Box display="flex" gap={0.5} flexWrap="wrap" mb={1.5}>
              {([TrainingValidityStatus.VALID, TrainingValidityStatus.WARNING, TrainingValidityStatus.EXPIRED] as const).map((s) =>
                countByValidity[s] ? (
                  <Chip
                    key={s}
                    label={`${TrainingValidityStatusLabels[s]}: ${countByValidity[s]}`}
                    size="small"
                    color={TrainingValidityStatusColors[s]}
                    variant="outlined"
                  />
                ) : null
              )}
            </Box>

            {/* List */}
            <Stack spacing={0.75}>
              {paged.map((t) => (
                <Box
                  key={t.id}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  sx={{ py: 0.5, px: 1, borderRadius: 1, bgcolor: 'action.hover' }}
                >
                  <Typography variant="caption" sx={{ flex: 1 }} noWrap title={t.name}>
                    {t.name}
                  </Typography>
                  <Chip
                    label={t.type ? (TrainingNRTypeLabels[t.type] || t.type) : '-'}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                  <Chip
                    label={TrainingValidityStatusLabels[t.validityStatus]}
                    size="small"
                    color={TrainingValidityStatusColors[t.validityStatus]}
                    sx={{ height: 20, fontSize: '0.7rem' }}
                  />
                </Box>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5} mt={1}>
                <Typography variant="caption" color="text.secondary">
                  {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, trainings.length)} de {trainings.length}
                </Typography>
                <IconButton size="small" disabled={page === 0} onClick={() => setPage(page - 1)}>
                  <ChevronLeft fontSize="small" />
                </IconButton>
                <IconButton size="small" disabled={page >= totalPages - 1} onClick={() => setPage(page + 1)}>
                  <ChevronRight fontSize="small" />
                </IconButton>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};
